import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, SafeAreaView, ScrollView, ActivityIndicator } from 'dripsy';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { DigitalDepositStackParamList } from './type';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { Icon } from '@/design-system/components/icons/Icon';
import { Blockchain, DigitalAsset, DigitalDepositAddress } from '../../../../../../packages/utx-access-client/src/react';
import { useDepositAddresses } from '../details/hooks/useAccount.hook';
import QRCode from 'qrcode';
import { Divider } from '@/design-system/components/Divider';
import { ChainSelector } from '../../components/chainSelector/ChainSelector';

export interface DigitalDepositProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  account: string;
  refresh?: () => void;
  record?: DigitalAsset & { assetChains: string[]; chains?: Blockchain[] };
}

export const DigitalDepositNativeAddressScreen: React.FC<DigitalDepositProps> = (props) => {
  const { onClose, onLayout, account, refresh, record } = props;
  const { t } = useTranslation('digital-injection');
  const { container, body, textMb } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<DigitalDepositStackParamList>>();

  const { theme } = useDripsyTheme();

  const { queryDepositAddresses } = useDepositAddresses();
  const [chainsDisplay, setChainsDisplay] = useState<{ chain: string; chainName: string | undefined }[]>([]);
  const [selectedChain, setSelectedChain] = useState<string | undefined>(undefined);
  const [address, setAddress] = useState<(DigitalDepositAddress & { chainName: string | undefined }) | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const qrcodeRef = useRef(null);
  useEffect(() => {
    if (!selectedChain) return;
    queryDepositAddresses({ asset: record?.symbol, chain: selectedChain, accountId: [account] })
      .then((response) => {
        if (response.data && response.data.addresses) {
          let addresses = response.data.addresses;
          let addressesDisplayFlag: (DigitalDepositAddress & { chainName: string | undefined })[] = [];
          addresses?.forEach((a) => {
            let chain = record?.chains?.find((c) => c?.chain == a?.chain);
            if (chain) {
              addressesDisplayFlag.push({ ...a, chainName: chain?.name });
            }
          });
          addressesDisplayFlag = addressesDisplayFlag.sort(
            (a, b) => Number(a?.chainName?.charCodeAt(0)) - Number(b?.chainName?.charCodeAt(0))
          );
          setAddress(addressesDisplayFlag[0]);
        } else {
          setAddress(undefined);
        }
      })
      .catch((error) => {
        setAddress(undefined);
      });
  }, [selectedChain]);

  useEffect(() => {
    let chainsDisplayFlag: { chain: string; chainName: string | undefined }[] = [];
    record?.assetChains.forEach((ac) => {
      let chain = record?.chains?.find((c) => c?.chain == ac);
      if (chain) {
        chainsDisplayFlag.push({ chain: ac, chainName: chain?.name });
      }
    });
    if (chainsDisplayFlag.length) {
      setSelectedChain(chainsDisplayFlag[0].chain);
    }
    setChainsDisplay(chainsDisplayFlag);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!address) return;
    QRCode.toCanvas(
      qrcodeRef.current,
      address?.address || '',
      {
        width: 120,
        margin: 0,
        errorCorrectionLevel: 'H',
        color: { light: '#ffffff', dark: '#000000' }
      },
      (error) => {
        if (error) console.log(error);
      }
    );
  }, [address]);

  return (
    <SafeAreaView sx={container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={{ height: '100%', backgroundColor: '$white' }}>
          <PageHeaderModalNative>
            <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
              <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
                <UniIconButton
                  icon="arrowLeft"
                  options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                  onPress={onClose}></UniIconButton>
              </View>
              <View sx={{ textAlign: 'center' }}>
                <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
                  {t('digital-injection::injection-instruction.title--injection-instruction', { asset: record?.name })}
                </TextTitle>
              </View>
            </View>
          </PageHeaderModalNative>
          <View sx={body}>
            <TextTitle sx={textMb} semiBold>
              {t('digital-injection::injection-instruction.text--select-a-network', { asset: record?.name })}
            </TextTitle>
            <View sx={{ gap: '$1', zIndex: 99 }}>
              <TextBody>{t('digital-injection::glossary.network')}</TextBody>
              <ChainSelector
                value={address?.chain}
                chainsDisplay={chainsDisplay}
                onValueChange={() => {}}
                onChainChange={(value) => {
                  value && setSelectedChain(value.chain);
                }}
              />
              {!isLoading ? (
                !chainsDisplay.length && (
                  <TextCaption danger sx={{ mt: '$1' }}>
                    {t('digital-injection::glossary.no-network-available-error')}
                  </TextCaption>
                )
              ) : (
                <View sx={{ padding: '$5' }}>
                  <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
                </View>
              )}
            </View>
            {address && (
              <>
                <View sx={{ alignItems: 'center' }}>
                  <View sx={{ display: 'inline-block', position: 'relative' }}>
                    <canvas ref={qrcodeRef}></canvas>
                  </View>
                </View>
                <View sx={{ gap: '$1' }}>
                  <TextBody>{t('digital-injection::glossary.deposit-address')}</TextBody>
                  <View
                    sx={{
                      gap: '$2',
                      flexDirection: 'row',
                      backgroundColor: '#f5f5f5',
                      height: '$9',
                      lineHeight: '$7',
                      alignItems: 'center',
                      borderRadius: '$radius1',
                      justifyContent: 'space-between'
                    }}>
                    <TextCaption sx={{ pl: '$2', overflow: 'hidden', height: '$9', lineHeight: '$7' }}>{address?.address}</TextCaption>
                    <UniIconButton
                      icon="copyWeb"
                      onPress={() => {
                        navigator.clipboard.writeText(address?.address || '');
                      }}
                      sx={{ pr: '$2' }}
                    />
                  </View>
                </View>
                <View>
                  <View sx={{ flexDirection: 'row', gap: '$1' }}>
                    <Icon name="note" />
                    <TextBody bold>{t('digital-injection::injection-instruction.text--important')}</TextBody>
                  </View>
                  <TextCaption>
                    {t('digital-injection::injection-instruction.text--accepted-address-one')} <TextBody semiBold>{record?.name}s</TextBody>{' '}
                    {t('digital-injection::injection-instruction.text--accepted-address-two')}{' '}
                    <TextBody semiBold>{address?.chainName}</TextBody>{' '}
                    {t('digital-injection::injection-instruction.text--accepted-address-three')}
                  </TextCaption>
                </View>
              </>
            )}
            <Divider sx={{ margin: 'auto', backgroundColor: '#f0f0f0' }} />
            <View sx={{ gap: '$1' }}>
              <TextBody bold>{t('digital-injection::injection-instruction.text--notice')}</TextBody>
              <View sx={{ flexDirection: 'row', gap: '$1' }}>
                <TextCaption>1.</TextCaption>
                <TextCaption>{t('digital-injection::injection-instruction.text--notice-one')}</TextCaption>
              </View>
              <View sx={{ flexDirection: 'row', gap: '$1' }}>
                <TextCaption>2.</TextCaption>
                <TextCaption>{t('digital-injection::injection-instruction.text--notice-two')}</TextCaption>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  body: {
    px: 16,
    py: 20,
    gap: '$5'
  },
  textMb: {}
};
